import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios, { API_URL } from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import './Dashboard.css';

const DocumentosSoporte = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedConcesionario, setSelectedConcesionario] = useState(null);

  // States para Cuenta de Cobro / Documentos Soporte
  const [cuentaCobroTramites, setCuentaCobroTramites] = useState([]);
  const [loadingCuentaCobro, setLoadingCuentaCobro] = useState(false);
  const [filterDocStatus, setFilterDocStatus] = useState('todos'); // 'todos' | 'completo' | 'incompleto'
  const [filterEstadoTramite, setFilterEstadoTramite] = useState('todos'); // 'todos' | 'tramite_ejecutado' | etc.
  const [searchCuentaCobro, setSearchCuentaCobro] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [selectedCuentaCobroIds, setSelectedCuentaCobroIds] = useState([]);
  const [generandoPaquete, setGenerandoPaquete] = useState(false);

  // Preview Modal state
  const [previewDoc, setPreviewDoc] = useState(null); // { title: string, url: string }

  // Upload Missing Document Modal state
  const [uploadModal, setUploadModal] = useState(null); // { tramite: object }
  const [uploadDocKey, setUploadDocKey] = useState('tarjeta_propiedad');
  const [tarjetaMode, setTarjetaMode] = useState('doble_cara'); // 'doble_cara' | 'unico'
  const [singleFile, setSingleFile] = useState(null);
  const [frontalFile, setFrontalFile] = useState(null);
  const [traseraFile, setTraseraFile] = useState(null);
  const [subiendoDoc, setSubiendoDoc] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/liquidacion/stats');
      setStats(res.data);
      setError('');
      // Si el usuario es de un concesionario específico y solo hay 1 en el resumen, seleccionarlo automáticamente
      if (res.data.length === 1 && user?.concesionario_id) {
        setSelectedConcesionario(res.data[0]);
      }
    } catch (err) {
      setError('Error al cargar datos de concesionarios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCuentaCobro = async (concesionarioId) => {
    try {
      setLoadingCuentaCobro(true);
      const queryParams = new URLSearchParams({
        filter_doc_status: filterDocStatus,
        estado_tramite: filterEstadoTramite,
        search: searchCuentaCobro,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }).toString();

      const res = await axios.get(`/api/liquidacion/concesionario/${concesionarioId}/cuenta-cobro?${queryParams}`);
      setCuentaCobroTramites(res.data);
      setSelectedCuentaCobroIds([]);
    } catch (err) {
      console.error(err);
      alert('Error al cargar trámites para Documentos Soporte');
    } finally {
      setLoadingCuentaCobro(false);
    }
  };

  useEffect(() => {
    if (selectedConcesionario) {
      loadCuentaCobro(selectedConcesionario.concesionario_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConcesionario, filterDocStatus, filterEstadoTramite, searchCuentaCobro, fechaInicio, fechaFin]);

  const handleExportExcel = () => {
    if (cuentaCobroTramites.length === 0) {
      alert('No hay trámites para exportar.');
      return;
    }

    const dataToExport = cuentaCobroTramites.map(t => {
      const checklistDocs = Object.values(t.checklist || {});
      const isComplete = t.doc_status === 'completo' || (checklistDocs.length > 0 && checklistDocs.every(doc => !doc.required || doc.present));
      
      const docsSummary = Object.entries(t.checklist || {})
        .filter(([_, doc]) => !doc.is_na && doc.required)
        .map(([_, doc]) => `${doc.label}: ${doc.present ? 'OK' : 'FALTANTE'}`)
        .join(' | ');

      return {
        'ID Trámite': t.id,
        'Placa': t.plate || 'Sin Placa',
        'Propietario': t.propietario || 'N/A',
        'Documento Propietario': t.documento || 'N/A',
        'Tipo de Trámite': t.tipo_tramite,
        'Tránsito': t.transito_name || 'N/A',
        'Estado Trámite': t.estado_tramite,
        'Estado Expediente': isComplete ? 'COMPLETO' : 'INCOMPLETO',
        'Checklist Documental': docsSummary,
        'Fecha Registro': formatDateShort(t.created_at)
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Documentos_Soporte');
    const concName = selectedConcesionario?.concesionario_name ? selectedConcesionario.concesionario_name.replace(/[^a-z0-9]/gi, '_') : 'Concesionario';
    XLSX.writeFile(workbook, `Documentos_Soporte_${concName}_${new Date().toLocaleDateString('es-CO').replace(/\//g, '-')}.xlsx`);
  };

  const handleSelectConcesionario = (stat) => {
    setSelectedConcesionario(stat);
  };

  const handleSelectAllCuentaCobro = (e) => {
    if (e.target.checked) {
      setSelectedCuentaCobroIds(cuentaCobroTramites.map(t => t.id));
    } else {
      setSelectedCuentaCobroIds([]);
    }
  };

  const handleSelectTramiteCuentaCobro = (id) => {
    if (selectedCuentaCobroIds.includes(id)) {
      setSelectedCuentaCobroIds(selectedCuentaCobroIds.filter(tId => tId !== id));
    } else {
      setSelectedCuentaCobroIds([...selectedCuentaCobroIds, id]);
    }
  };

  const handleGenerarPaquete = async () => {
    if (selectedCuentaCobroIds.length === 0) {
      alert('Por favor selecciona al menos un trámite para generar el paquete');
      return;
    }

    try {
      setGenerandoPaquete(true);
      const res = await axios.post(
        '/api/liquidacion/generar-paquete',
        { tramite_ids: selectedCuentaCobroIds },
        { responseType: 'blob' }
      );

      let filename = selectedCuentaCobroIds.length === 1 ? 'Tramite_Unificado.pdf' : 'Cuenta_de_Cobro.zip';
      const disposition = res.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Error al generar paquete de Documentos Soporte:', err);
      let errMsg = err.message;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.message) errMsg = parsed.message;
        } catch (e) {
          // Keep err.message
        }
      }
      alert('❌ Error al generar el paquete de documentos unificados: ' + errMsg);
    } finally {
      setGenerandoPaquete(false);
    }
  };

  const handleOpenPreview = (docLabel, docUrl, plate) => {
    if (!docUrl) {
      alert(`⚠️ El documento '${docLabel}' aún no ha sido cargado.`);
      return;
    }
    let fullUrl = docUrl.startsWith('http') ? docUrl : `${API_URL}${docUrl}`;
    const token = localStorage.getItem('token');
    if (token && !fullUrl.includes('token=')) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + `token=${token}`;
    }
    setPreviewDoc({
      title: `${docLabel} (${plate || 'Sin Placa'})`,
      url: fullUrl
    });
  };

  const getAvailableDocsForUpload = (t) => {
    if (!t || !t.checklist) return [];
    
    const missing = Object.entries(t.checklist)
      .filter(([key, doc]) => doc.required && !doc.present && !doc.is_na)
      .map(([key, doc]) => ({ key, label: doc.label }));

    if (missing.length > 0) {
      return missing;
    }

    return Object.entries(t.checklist)
      .map(([key, doc]) => ({ key, label: doc.label }));
  };

  const handleOpenUploadModal = (t) => {
    const available = getAvailableDocsForUpload(t);
    const initialKey = available.length > 0 ? available[0].key : 'tarjeta_propiedad';

    setUploadModal({ tramite: t });
    setUploadDocKey(initialKey);
    setTarjetaMode('doble_cara');
    setSingleFile(null);
    setFrontalFile(null);
    setTraseraFile(null);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadModal?.tramite) return;

    if (uploadDocKey === 'tarjeta_propiedad' && tarjetaMode === 'doble_cara') {
      if (!frontalFile || !traseraFile) {
        alert('Por favor selecciona los archivos frontal y trasero de la Tarjeta de Propiedad');
        return;
      }
    } else {
      if (!singleFile) {
        alert('Por favor selecciona el archivo a subir');
        return;
      }
    }

    try {
      setSubiendoDoc(true);
      const data = new FormData();
      data.append('tramite_id', uploadModal.tramite.id);
      data.append('tipo_documento_key', uploadDocKey);
      data.append('tipo_documento', uploadDocKey);

      if (uploadDocKey === 'tarjeta_propiedad' && tarjetaMode === 'doble_cara') {
        data.append('tarjeta_frontal', frontalFile);
        data.append('tarjeta_trasera', traseraFile);
      } else {
        data.append('archivo', singleFile);
      }

      await axios.post('/api/liquidacion/subir-documento-faltante', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('✅ Documento cargado exitosamente.');
      setUploadModal(null);
      loadCuentaCobro(selectedConcesionario.concesionario_id);
    } catch (err) {
      console.error(err);
      alert('❌ Error al subir documento: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubiendoDoc(false);
    }
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatFullName = (username) => {
    if (!username) return 'N/A';
    return username
      .replace(/[._]/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="📁 Documentos Soporte" />
        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          
          {!selectedConcesionario ? (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Resumen de Concesionarios</h2>
                  <p className="subtitle">Selecciona un concesionario para verificar sus documentos soporte y paquete de cobro</p>
                </div>
                <button onClick={fetchStats} className="btn-secondary">
                  🔄 Actualizar
                </button>
              </div>

              {loading ? (
                <p className="loading">Cargando concesionarios...</p>
              ) : (
                <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {stats.map((stat) => (
                    <div 
                      key={stat.concesionario_id} 
                      className="stat-card" 
                      style={{ cursor: 'pointer', borderLeft: '4px solid #10b981' }}
                      onClick={() => handleSelectConcesionario(stat)}
                    >
                      <div className="stat-details" style={{ width: '100%' }}>
                        <span className="stat-label" style={{ fontSize: '1.1rem', color: '#1e293b', marginBottom: '10px' }}>
                          🏢 {stat.concesionario_name}
                        </span>
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#047857', fontWeight: 'bold' }}>
                          👉 Haz clic para verificar expedientes completos
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {stats.length === 0 && (
                    <div className="no-data" style={{ gridColumn: '1 / -1' }}>No hay concesionarios disponibles.</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="page-header">
                <div className="header-title">
                  <h2>Concesionario: {selectedConcesionario.concesionario_name}</h2>
                  <p className="subtitle">Verificación de expediente digital y generación de paquete de cuenta de cobro</p>
                </div>
                {(!user?.concesionario_id || user?.role === 'superadmin' || user?.profile_name === 'superadmin' || user?.profile_name === 'auxiliar transiapp') && (
                  <button onClick={() => setSelectedConcesionario(null)} className="btn-secondary">
                    ⬅️ Volver a Resumen
                  </button>
                )}
              </div>

              <div className="dashboard-controls" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                <div className="quick-filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button 
                    className={`btn-filter ${filterDocStatus === 'todos' ? 'active' : ''}`}
                    onClick={() => setFilterDocStatus('todos')}
                  >
                    Todos
                  </button>
                  <button 
                    className={`btn-filter ${filterDocStatus === 'completo' ? 'active' : ''}`}
                    onClick={() => setFilterDocStatus('completo')}
                    style={filterDocStatus === 'completo' ? { background: '#ecfdf5', color: '#047857', border: '1px solid #10b981' } : {}}
                  >
                    ✅ Expedientes Completos
                  </button>
                  <button 
                    className={`btn-filter ${filterDocStatus === 'incompleto' ? 'active' : ''}`}
                    onClick={() => setFilterDocStatus('incompleto')}
                    style={filterDocStatus === 'incompleto' ? { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' } : {}}
                  >
                    ⚠️ Expedientes Incompletos
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>📋 Estado Trámite:</label>
                    <select
                      className="form-input"
                      value={filterEstadoTramite}
                      onChange={(e) => setFilterEstadoTramite(e.target.value)}
                      style={{ padding: '6px 12px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                    >
                      <option value="todos">Todos los Estados</option>
                      <option value="tramite_ejecutado">🎉 Trámite Ejecutado</option>
                      <option value="pre_asignado">🚗 Pre-asignado</option>
                      <option value="radicado">📝 Radicado</option>
                      <option value="novedad_cupl_pagado">💳 CUPL Pagado</option>
                      <option value="novedad_soat_vigente">🛡️ SOAT Vigente</option>
                      <option value="novedad_pendiente_cupl">💳 Pendiente CUPL</option>
                      <option value="novedad_pendiente_soat">📄 Pendiente SOAT</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>📅 Desde:</label>
                    <input 
                      type="date" 
                      className="form-input"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      style={{ padding: '5px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Hasta:</label>
                    <input 
                      type="date" 
                      className="form-input"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      style={{ padding: '5px 10px', fontSize: '13px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    />
                    {(fechaInicio || fechaFin) && (
                      <button 
                        className="btn-secondary" 
                        onClick={() => { setFechaInicio(''); setFechaFin(''); }}
                        title="Limpiar fechas"
                        style={{ padding: '5px 8px', fontSize: '12px', background: '#f1f5f9', border: '1px solid #cbd5e1', cursor: 'pointer' }}
                      >
                        ❌ Limpiar
                      </button>
                    )}
                  </div>
                </div>

                <div className="filter-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input 
                    type="text" 
                    placeholder="🔍 Placa, ID, Propietario..." 
                    className="form-input" 
                    value={searchCuentaCobro}
                    onChange={(e) => setSearchCuentaCobro(e.target.value)}
                    style={{ width: '220px' }}
                  />
                  <button 
                    className="btn-secondary"
                    onClick={handleExportExcel}
                    style={{ background: '#16a34a', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    📊 Exportar Excel
                  </button>
                  <button 
                    className="btn-primary"
                    onClick={handleGenerarPaquete}
                    disabled={selectedCuentaCobroIds.length === 0 || generandoPaquete}
                    style={{ background: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {generandoPaquete ? '⌛ Generando Paquete...' : `📦 Descargar Paquete de Cobro (${selectedCuentaCobroIds.length})`}
                  </button>
                </div>
              </div>

              {loadingCuentaCobro ? (
                <p className="loading">Cargando expedientes...</p>
              ) : (
                <div className="table-wrapper">
                  <div className="table-responsive">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px', textAlign: 'center' }}>
                            <input 
                              type="checkbox" 
                              onChange={handleSelectAllCuentaCobro}
                              checked={cuentaCobroTramites.length > 0 && selectedCuentaCobroIds.length === cuentaCobroTramites.length}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </th>
                          <th>ID / Placa</th>
                          <th>Propietario / CC</th>
                          <th>Tipo Trámite / Fecha</th>
                          <th>Checklist Soporte</th>
                          <th>Estado Expediente</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cuentaCobroTramites.map(t => {
                          const checklistDocs = Object.values(t.checklist || {});
                          const isComplete = t.doc_status === 'completo' || (checklistDocs.length > 0 && checklistDocs.every(doc => !doc.required || doc.present));
                          return (
                            <tr key={t.id} className={selectedCuentaCobroIds.includes(t.id) ? 'selected-row' : ''}>
                              <td style={{ textAlign: 'center' }}>
                                <input 
                                  type="checkbox"
                                  checked={selectedCuentaCobroIds.includes(t.id)}
                                  onChange={() => handleSelectTramiteCuentaCobro(t.id)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                              </td>
                              <td>
                                <strong>{t.plate || 'Sin Placa'}</strong>
                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{t.id}</div>
                              </td>
                              <td>
                                <strong style={{ color: '#1e293b', display: 'block', fontSize: '13px' }}>{t.propietario || 'N/A'}</strong>
                                <span style={{ fontSize: '11px', color: '#64748b' }}>CC/NIT: {t.documento || 'N/A'}</span>
                              </td>
                              <td>
                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{t.tipo_tramite}</div>
                                <div style={{ fontSize: '11px', color: '#64748b' }}>{formatDateShort(t.created_at)}</div>
                              </td>
                              <td>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', fontSize: '11px' }}>
                                  {Object.entries(t.checklist || {})
                                    .filter(([key, doc]) => !doc.is_na && doc.required)
                                    .map(([key, doc]) => (
                                      <div 
                                        key={key} 
                                        onClick={() => doc.present ? handleOpenPreview(doc.label, doc.url, t.plate) : null}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 8px',
                                          borderRadius: '6px',
                                          cursor: doc.present ? 'pointer' : 'default',
                                          background: doc.present ? '#dcfce7' : '#fee2e2',
                                          color: doc.present ? '#15803d' : '#991b1b',
                                          border: `1px solid ${doc.present ? '#bbf7d0' : '#fecdd3'}`
                                        }}
                                        title={doc.present ? 'Haga clic para previsualizar documento' : 'Documento faltante'}
                                      >
                                        <span>{doc.present ? '✅' : '❌'}</span>
                                        <span style={{ fontWeight: '600' }}>{doc.label}</span>
                                      </div>
                                    ))}
                                </div>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '800',
                                  background: isComplete ? '#dcfce7' : '#fee2e2',
                                  color: isComplete ? '#15803d' : '#991b1b',
                                  border: `1px solid ${isComplete ? '#bbf7d0' : '#fecdd3'}`
                                }}>
                                  {isComplete ? '✅ COMPLETO' : '⚠️ INCOMPLETO'}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn-secondary"
                                  onClick={() => handleOpenUploadModal(t)}
                                  style={{ padding: '6px 10px', fontSize: '12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: '700' }}
                                >
                                  📤 Cargar Faltante
                                </button>
                              </td>
                            </tr>
                          );
                        })}

                        {cuentaCobroTramites.length === 0 && (
                          <tr>
                            <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                              No hay trámites registrados con los filtros seleccionados.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Previsualización de Documentos */}
      {previewDoc && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '850px', width: '95%', height: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>👁️ {previewDoc.title}</h3>
              <button className="close-btn" onClick={() => setPreviewDoc(null)}>&times;</button>
            </div>
            <div style={{ flex: 1, marginTop: '15px', background: '#f8fafc', borderRadius: '8px', overflow: 'hidden' }}>
              {previewDoc.url.toLowerCase().includes('.pdf') ? (
                <iframe src={previewDoc.url} title="Vista previa PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px' }}>
                  <img src={previewDoc.url} alt="Documento Soporte" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
              <a href={previewDoc.url} download target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ marginRight: '10px', textDecoration: 'none' }}>
                📥 Descargar Original
              </a>
              <button className="btn-secondary" onClick={() => setPreviewDoc(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cargar Documento Faltante */}
      {uploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <h3>📤 Cargar Documento Soporte</h3>
              <button className="close-btn" onClick={() => setUploadModal(null)}>&times;</button>
            </div>
            <form onSubmit={handleUploadSubmit} style={{ marginTop: '15px' }}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Seleccione el Documento a Cargar:</label>
                <select 
                  className="form-input"
                  value={uploadDocKey}
                  onChange={(e) => setUploadDocKey(e.target.value)}
                >
                  {getAvailableDocsForUpload(uploadModal.tramite).map(doc => (
                    <option key={doc.key} value={doc.key}>{doc.label}</option>
                  ))}
                </select>
              </div>

              {uploadDocKey === 'tarjeta_propiedad' && (
                <div className="form-group" style={{ marginBottom: '15px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px', fontSize: '13px' }}>Formato de Tarjeta de Propiedad:</label>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <label style={{ cursor: 'pointer', fontSize: '13px' }}>
                      <input 
                        type="radio" 
                        name="tarjetaMode" 
                        value="doble_cara" 
                        checked={tarjetaMode === 'doble_cara'} 
                        onChange={() => setTarjetaMode('doble_cara')}
                        style={{ marginRight: '5px' }}
                      />
                      Doble Cara (2 Archivos)
                    </label>
                    <label style={{ cursor: 'pointer', fontSize: '13px' }}>
                      <input 
                        type="radio" 
                        name="tarjetaMode" 
                        value="unico" 
                        checked={tarjetaMode === 'unico'} 
                        onChange={() => setTarjetaMode('unico')}
                        style={{ marginRight: '5px' }}
                      />
                      Un solo PDF / Archivo
                    </label>
                  </div>
                </div>
              )}

              {uploadDocKey === 'tarjeta_propiedad' && tarjetaMode === 'doble_cara' ? (
                <>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '13px' }}>📄 Cara Frontal:</label>
                    <input 
                      type="file" 
                      accept=".pdf,.png,.jpg,.jpeg" 
                      onChange={(e) => setFrontalFile(e.target.files[0])}
                      required 
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '13px' }}>📄 Cara Trasera:</label>
                    <input 
                      type="file" 
                      accept=".pdf,.png,.jpg,.jpeg" 
                      onChange={(e) => setTraseraFile(e.target.files[0])}
                      required 
                    />
                  </div>
                </>
              ) : (
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Seleccione Archivo (PDF/Imagen):</label>
                  <input 
                    type="file" 
                    accept=".pdf,.png,.jpg,.jpeg" 
                    onChange={(e) => setSingleFile(e.target.files[0])}
                    required 
                  />
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button type="button" className="btn-secondary" onClick={() => setUploadModal(null)} disabled={subiendoDoc}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={subiendoDoc}>
                  {subiendoDoc ? 'Cargando...' : 'Subir Documento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosSoporte;
